import type { Report } from '../types';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * DER ANKER — auf welchem Serverstand ein Schreibvorgang aufsetzt
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `saveProjectToCloud` schreibt `projects.data` KOMPLETT und bisher ohne jede
 * Bedingung: `update ... where id = ?`. Damit darf jeder Client jeden
 * beliebig alten Stand zurueckschreiben. Das ist die Ursache der Klasse
 * „Buchungszeilen verschwinden": am 31.08.2026 hat die mobile
 * Offline-Warteschlange stundenalte Blobs von 11-16 Projekten im Sekundentakt
 * hochgeschoben; der Riegel aus Nr. 44 hat die Arbeitszeilen gerettet und die
 * Abrechnungszeilen (`BU-`/`ALT-`) fallen lassen — 19 Zeilen in 5 Projekten.
 *
 * ─── Das nimmt „last write wins" NICHT zurueck ─────────────────────────────
 * Der Konfliktdialog wurde 2026 abgeschafft, und das bleibt richtig: er hat
 * die Mitarbeiter mehr verwirrt als geschuetzt, und fuer einen FRISCHEN
 * Schreibvorgang ist der letzte Schreiber tatsaechlich der aktuellste. Fuer
 * einen drei Stunden alten Blob ist der letzte Schreiber dagegen der AELTESTE
 * Stand im Raum — und genau diese beiden Faelle konnte der Speicherweg bisher
 * nicht auseinanderhalten. Der Anker macht sie unterscheidbar; was bei einem
 * Konflikt geschieht, entscheidet der jeweilige Schreibweg (die Warteschlange
 * fuehrt zusammen, ein interaktives Speichern meldet).
 *
 * ─── Warum eine Registratur und kein Parameter an 40 Aufrufstellen ─────────
 * `saveProjectToCloud` wird an 40 Stellen in 23 Dateien gerufen. Jede einzeln
 * um einen Anker zu erweitern hiesse, 40 Gelegenheiten zu schaffen, ihn zu
 * vergessen — und eine vergessene Stelle ist genau der Schreibweg, der die
 * Zeilen wieder verliert. Deshalb merkt sich dieses Modul den zuletzt
 * GESEHENEN Serverstand je Projekt, gespeist aus den Ladewegen und aus jeder
 * bestaetigten Antwort des Servers; `saveProjectToCloud` greift von sich aus
 * darauf zu.
 *
 * ─── Was der Anker ausdruecklich NICHT leistet ─────────────────────────────
 * Er ist opportunistisch. Wer ein Projekt in dieser Sitzung nie geladen hat
 * (Admin-Listen, Hintergrunddienste), hat keinen Anker, und sein Schreiben
 * laeuft wie bisher ohne Bedingung durch. Das ist Absicht: ein erfundener
 * Anker wuerde entweder alles blockieren oder nichts pruefen. Die
 * Warteschlange — der Weg, der den Schaden angerichtet hat — traegt ihren
 * Anker dagegen IMMER selbst mit sich (sie entsteht aus einem geladenen
 * Projekt) und reicht ihn ausdruecklich herein.
 *
 * Der Speicher ist pro Browser-Tab und ueberlebt kein Neuladen. Auch das ist
 * richtig so: nach einem Neuladen ist der erste Ladevorgang der neue Anker.
 */

/** Der zuletzt gesehene Serverstand je Projekt (`projects.updated_at`). */
const anker = new Map<string, string>();

/**
 * Was ein Schreibweg zum Anker zu sagen hat.
 *
 * `expectedUpdatedAt` schlaegt die Registratur — die Warteschlange kennt den
 * Stand, auf dem IHR Eintrag beruht, besser als der laufende Tab, in dem
 * inzwischen ein anderes Projekt offen sein kann.
 * `anchor: 'off'` schaltet die Bedingung fuer genau diesen Aufruf ab; das ist
 * fuer Wiederherstellungen gedacht, die bewusst ueber einen fremden Stand
 * schreiben, und muss an der Aufrufstelle begruendet sein.
 */
export interface SaveAnchorOptions {
    expectedUpdatedAt?: string | null;
    anchor?: 'auto' | 'off';
    /**
     * Was geschehen soll, wenn der Anker nicht trifft.
     *
     * `'salvage'` (Vorgabe) ist fuer die INTERAKTIVEN Schreibwege: einmal den
     * Serverstand holen, jede Protokollzeile uebernehmen, die dieser Stand
     * nicht kennt, und mit frischem Anker erneut schreiben. Der Anwender merkt
     * nichts, und keine Buchung geht verloren.
     *
     * Warum nicht einfach melden: der Anker trifft auch dann nicht, wenn
     * jemand voellig unbeteiligt am selben Projekt gespeichert hat — das
     * eigene Telefon zum Beispiel, das im Hintergrund seine Warteschlange
     * leert, waehrend derselbe Mitarbeiter am PC weiterarbeitet. Eine
     * Fehlermeldung waere dort keine Auskunft, sondern eine Sackgasse: der
     * Anwender kann nichts entscheiden, und die Arbeit bliebe liegen.
     *
     * `'throw'` ist fuer die Warteschlange. Sie kennt ihren Ausgangsstand und
     * kann deshalb WIRKLICH zusammenfuehren statt nur zu bergen — der
     * Unterschied ist, ob eine fehlende Zeile als „entfernt" oder als „nie
     * gesehen" gilt. Sie braucht den Konflikt als Signal.
     */
    onConflict?: 'salvage' | 'throw';
    /**
     * Was mit den ZAEHLERN der Positionen geschieht (`project_services.
     * quantity_used`, `quantity_billed`, `status`).
     *
     * `'write'` (Vorgabe): wie bisher, der lokale Stand wird geschrieben.
     *
     * `'keep-server'`: die drei Schluessel werden aus den Zeilen WEGGELASSEN,
     * und save_project_children_tx laesst einen fehlenden Schluessel stehen —
     * der Server behaelt seine Werte. Gedacht fuer jeden Schreibvorgang, der
     * nachweislich auf einem UEBERHOLTEN Stand aufsetzt: am 07.09.2026 haben
     * alte Tabs und alte Warteschlangeneintraege in 24 Projekten das
     * Protokoll geborgen (die fremden Buchungszeilen blieben) und im selben
     * Zug den Zaehler aus ihrem alten Stand darueber geschrieben — Protokoll
     * N, quantity_used N-1, und die Begehung fehlte in der Rechnung. Der Riegel
     * Nr. 78 faengt das inzwischen in der Datenbank; hier wird es gar nicht
     * erst versucht. cloudService setzt den Wert selbst, sobald die Bergung
     * gelaufen ist; die Warteschlange reicht ihn beim Zusammenfuehren herein.
     */
    counters?: 'write' | 'keep-server';
    /**
     * Wie viel dieses Speichern übertragen darf.
     *
     * `'voll'` (Vorgabe): wie bisher — alle Kindtabellen, volle Zeile als
     * Antwort. Das gilt für JEDEN Aufrufer, der nichts sagt: Wiederherstellen,
     * Entwurf verwerfen, Umbuchen, Rechnungen, Bergung und Zusammenführung
     * schreiben bewusst den ganzen Stand.
     *
     * `'sparsam'`: unveränderte Kindtabellen bleiben weg, wenn der Tab
     * nachweisen kann, dass der Server sie schon so hält (Regeln in
     * `childSaveDelta.ts`), die Antwort kommt ohne den Blob
     * (`schmaleSpeicherAntwort.ts`), und die Besitzerspalte wird nicht erst
     * gelesen, um sie unverändert zurückzuschreiben. Gedacht für den
     * Normalfall der Warteschlange (useOfflineSync).
     */
    uebertragung?: 'voll' | 'sparsam';
    /**
     * Was der Abgleich der Kind-Tabellen `project_services` und
     * `project_notes` mit Zeilen tut, die der eigene Stand NICHT fuehrt.
     *
     * `'abgleichen'` (Vorgabe): wie bisher, sie werden geloescht — die eigene
     * Liste ist die Wahrheit.
     *
     * `'nur-ergaenzen'`: sie bleiben stehen, auch bei den folgenden Speichern
     * dieser Laufzeit (siehe `geschuetzteZeilen` in cloudService). Gedacht
     * fuer jeden Stand OHNE Ausgangsstand, der nachweislich ueberholt ist: er
     * kann eine fehlende Zeile nicht als „von mir entfernt" lesen, nur als
     * „nie gesehen". Am 16.09.2026 hat genau so ein Stand in 6185 zwei
     * abgerechnete Positionen (630 € netto) und drei Notizen geloescht —
     * das Protokoll hatte die Bergung gerettet, die Positionen nicht.
     * cloudService setzt den Wert selbst, sobald die Bergung gelaufen ist;
     * die Warteschlange reicht ihn bei der Bergung ohne Ausgangsstand herein.
     */
    kindZeilen?: 'abgleichen' | 'nur-ergaenzen';
}

/** Ein brauchbarer Zeitstempel — leer, `null` und Unsinn zaehlen nicht. */
const brauchbar = (value: unknown): value is string =>
    typeof value === 'string' && value.trim().length > 0;

/**
 * Merkt den Serverstand eines Projekts.
 *
 * Aufzurufen mit dem, was der Server WIRKLICH geantwortet hat, nie mit einem
 * selbst gebildeten `new Date()`: der Anker muss zeichengleich zu dem sein,
 * was in der Spalte steht, sonst trifft die Bedingung nie und jedes Speichern
 * meldete einen Konflikt.
 */
export const rememberProjectVersion = (projectId: unknown, updatedAt: unknown): void => {
    if (!brauchbar(projectId) || !brauchbar(updatedAt)) return;
    anker.set(projectId, updatedAt);
};

/** Der zuletzt gesehene Serverstand, oder `null`. */
export const knownProjectVersion = (projectId: unknown): string | null =>
    (brauchbar(projectId) ? anker.get(projectId) ?? null : null);

/** Vergisst einen Anker — nach einem Konflikt, den ein Aufrufer bewusst uebergeht. */
export const forgetProjectVersion = (projectId: unknown): void => {
    if (brauchbar(projectId)) anker.delete(projectId);
};

/** Nur fuer Tests und fuer den Wechsel des angemeldeten Benutzers. */
export const resetProjectVersions = (): void => { anker.clear(); };

/**
 * Welcher Anker fuer diesen Schreibvorgang gilt — oder `null` fuer „ohne
 * Bedingung schreiben, wie bisher".
 */
export const resolveSaveAnchor = (
    projectId: unknown,
    options?: SaveAnchorOptions | null,
): string | null => {
    if (options?.anchor === 'off') return null;
    if (options && 'expectedUpdatedAt' in options) {
        // Ausdruecklich mitgegeben — auch ein ausdrueckliches `null` gilt und
        // heisst „dieser Eintrag kennt keinen Stand", nicht „nimm den aus der
        // Registratur". Sonst schriebe ein Warteschlangeneintrag ohne Anker
        // gegen den Stand irgendeines anderen, gerade offenen Projekts.
        return brauchbar(options.expectedUpdatedAt) ? options.expectedUpdatedAt : null;
    }
    return knownProjectVersion(projectId);
};

/**
 * Ein Schreibvorgang, der auf einem ueberholten Serverstand aufsetzt.
 *
 * Traegt beide Staende: `expected` ist der, auf dem der Client aufgebaut hat,
 * `actual` der, den der Server inzwischen fuehrt. Ein Aufrufer, der zusammen-
 * fuehren will, braucht beide; ein Aufrufer, der nur melden will, nennt sie
 * dem Benutzer.
 */
export class ProjectSaveConflictError extends Error {
    readonly name = 'ProjectSaveConflictError';
    /** Fuer `classifySyncError`: das hier ist kein Netz- und kein Rechtefehler. */
    readonly code = 'project-conflict';

    constructor(
        readonly projectId: string,
        readonly expected: string,
        readonly actual: string | null,
    ) {
        super(
            `Das Projekt wurde zwischenzeitlich von anderer Stelle gespeichert `
            + `(erwartet ${expected}, tatsächlich ${actual ?? 'unbekannt'}). `
            + `Der lokale Stand wurde NICHT geschrieben.`,
        );
    }
}

export const isProjectSaveConflict = (e: unknown): e is ProjectSaveConflictError =>
    e instanceof ProjectSaveConflictError
    || (e as { code?: unknown } | null)?.code === 'project-conflict';

/** Das Wenige, das dieses Modul von einem Projektstand braucht. */
export type AnchoredReport = Pick<Report, 'metadata'>;
